import { useState, useRef, useEffect, useMemo } from 'react'
import { Send, Bot, User, Sparkles, Code, CheckCircle, Loader2, XCircle } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { useAgent } from 'agents/react'
import { AGENT_WORKER_HOST, isDev } from '../lib/config'

// Agent 状态类型定义（与服务端保持一致）
interface WorkflowStep {
  agent: string
  label: string
  status: 'waiting' | 'running' | 'complete' | 'failed'
  result?: string
  error?: string
}

interface WorkflowState {
  workflowId: string
  prompt: string
  steps: WorkflowStep[]
  currentStep: number
  status: 'idle' | 'running' | 'complete' | 'failed'
  finalResult?: string
  createdAt: string
  updatedAt: string
}

interface OrchestratorAgentState {
  id: string
  name: string
  status: string
  lastActivity: string
  metadata: Record<string, unknown>
  activeWorkflow: WorkflowState | null
  workflowHistory: string[]
}

// OrchestratorAgent 方法存根类型 (must include `state` for useAgent generic constraint)
interface OrchestratorAgentStub {
  readonly state: OrchestratorAgentState
  startWorkflow(prompt: string, sessionId: string): Promise<{ workflowId: string }>
  getWorkflowStatus(): WorkflowState | null
}

interface Message {
  id: string
  type: 'user' | 'system' | 'agent'
  agent?: string
  content: string
  timestamp: Date
  status?: 'streaming' | 'complete'
}

interface AgentStatus {
  name: string
  label: string
  status: 'waiting' | 'running' | 'complete' | 'failed'
  result?: string
}

export default function AgentTeam() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [sessionId] = useState(uuidv4())

  const agentHost = isDev ? undefined : AGENT_WORKER_HOST

  const {
    state,
    stub,
    ready,
    identified,
  } = useAgent<OrchestratorAgentStub, OrchestratorAgentState>({
    agent: 'orchestrator-agent',
    name: 'default',
    host: agentHost,
  })

  const getAgentLabel = useMemo(() => (agent: string): string => {
    const labels: Record<string, string> = {
      orchestrator: '总架构师',
      developer: '高级开发者',
      reviewer: '代码审查员',
      deployer: '部署专员',
    }
    return labels[agent] || agent
  }, [])

  // 监听工作流状态变化
  useEffect(() => {
    if (!state?.activeWorkflow) return

    const workflow = state.activeWorkflow
    const agents: AgentStatus[] = workflow.steps.map((s: WorkflowStep) => ({
      name: s.agent,
      label: s.label,
      status: s.status,
      result: s.result,
    }))

    setAgentStates(agents)

    // 当步骤完成时，添加消息
    for (const step of workflow.steps) {
      if (step.status === 'complete' && step.result && !processedSteps.current.has(step.agent + step.label)) {
        processedSteps.current.add(step.agent + step.label)
        const agentMessage: Message = {
          id: uuidv4(),
          type: 'agent',
          agent: getAgentLabel(step.agent),
          content: step.result,
          timestamp: new Date(),
          status: 'complete',
        }
        setMessages(prev => [...prev, agentMessage])
      }
    }

    // 工作流完成
    if (workflow.status === 'complete' && !workflowFinished) {
      setWorkflowFinished(true)
      setMessages(prev => [...prev, {
        id: uuidv4(),
        type: 'system',
        content: '工作流完成！所有智能体任务已完成。',
        timestamp: new Date(),
      }])
    }

    // 工作流失败
    if (workflow.status === 'failed') {
      setMessages(prev => [...prev, {
        id: uuidv4(),
        type: 'system',
        content: '工作流执行失败，请查看错误详情。',
        timestamp: new Date(),
      }])
    }
  }, [state?.activeWorkflow, getAgentLabel])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const [agentStates, setAgentStates] = useState<AgentStatus[]>([
    { name: 'orchestrator', label: '总架构师', status: 'waiting' },
    { name: 'developer', label: '高级开发者', status: 'waiting' },
    { name: 'reviewer', label: '代码审查员', status: 'waiting' },
    { name: 'deployer', label: '部署专员', status: 'waiting' },
  ])
  const [workflowFinished, setWorkflowFinished] = useState(false)
  const processedSteps = useRef(new Set<string>())
  const isRunning = state?.activeWorkflow?.status === 'running'
  const isConnected = identified

  const sendMessage = async () => {
    if (!input.trim() || isRunning) return

    if (!isConnected) {
      setMessages(prev => [...prev, {
        id: uuidv4(),
        type: 'system',
        content: '⚠️ Agent 未连接，请刷新页面重试。',
        timestamp: new Date(),
      }])
      return
    }

    const userMessage: Message = {
      id: uuidv4(),
      type: 'user',
      content: input,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')

    // 重置状态
    setAgentStates(prev => prev.map(a => ({ ...a, status: 'waiting', result: undefined })))
    setWorkflowFinished(false)
    processedSteps.current.clear()

    try {
      await stub.startWorkflow(input, sessionId)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误'
      setMessages(prev => [...prev, {
        id: uuidv4(),
        type: 'system',
        content: `启动工作流失败: ${errorMessage}`,
        timestamp: new Date(),
      }])
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  const getStatusBarColor = (status: string) => {
    switch (status) {
      case 'waiting':
        return 'bg-gray-600'
      case 'running':
        return 'bg-blue-500'
      case 'complete':
        return 'bg-green-500'
      case 'failed':
        return 'bg-red-500'
      default:
        return 'bg-gray-600'
    }
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4">
      {/* 左侧：智能体状态面板 */}
      <div className="w-64 bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-orange-500" />
          智能体团队
        </h3>

        <div className="space-y-3">
          {agentStates.map(agent => (
            <div key={agent.name} className="bg-gray-700 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{agent.label}</span>
                {getStatusIcon(agent.status)}
              </div>
              <div className={`h-1 rounded ${getStatusBarColor(agent.status)}`} />
            </div>
          ))}
        </div>
      </div>

      {/* 右侧：对话区域 */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 overflow-y-auto bg-gray-800 rounded-lg p-4 mb-4 space-y-4">
          {messages.map(msg => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.type !== 'user' && (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.type === 'agent' ? 'bg-orange-500' : 'bg-gray-600'
                }`}>
                  {msg.type === 'agent' ? <Code className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
                </div>
              )}
              <div
                className={`max-w-[70%] rounded-lg p-3 ${
                  msg.type === 'user'
                    ? 'bg-orange-500 text-white'
                    : msg.type === 'agent'
                    ? 'bg-gray-700 text-gray-100'
                    : 'bg-blue-900 text-blue-100'
                }`}
              >
                {msg.agent && (
                  <p className="text-xs opacity-60 mb-1">{msg.agent}</p>
                )}
                <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                <p className="text-xs opacity-60 mt-1">
                  {msg.timestamp.toLocaleTimeString()}
                </p>
              </div>
              {msg.type === 'user' && (
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" />
                </div>
              )}
            </div>
          ))}
          {isRunning && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="flex gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={isConnected ? '描述你的项目需求，智能体团队将协作完成...' : '⚠️ Agent 未连接，请刷新页面'}
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg p-3 resize-none focus:outline-none focus:border-orange-500"
            rows={2}
            disabled={isRunning || !isConnected}
          />
          <button
            onClick={sendMessage}
            disabled={isRunning || !input.trim() || !isConnected}
            className="px-6 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>

        {/* Connection Status */}
        {!isConnected && (
          <div className="mt-2 text-sm text-yellow-500">
            ⚠️ Agent 未连接，无法发送消息。请检查网络连接或刷新页面。
          </div>
        )}
      </div>
    </div>
  )
}
